import { useTranslation } from 'react-i18next';
import { IconDrag } from '@components/icons';
import { InputCheckbox } from '@components/input-checkbox';
import { DragHandleRenderProps } from '@components/sortable-list';
import { Addon } from '@domain/addon';
import { getDefaultAddonNameTranslation } from '@utils/get-default-addon-name-translation/get-default-addon-name-translation';
import { formatCurrency } from '@utils/price-formatter/price-formatter';
import classNames from 'classnames';

export interface AddOnRowProps {
  addon: Addon;
  checked: boolean;
  onToggle: (id: string) => void;
  disabled?: boolean;
  dragHandle?: DragHandleRenderProps;
  dataTestId?: string;
}

export const AddOnRow = ({ addon, checked, onToggle, disabled, dragHandle, dataTestId }: AddOnRowProps) => {
  const { t, i18n } = useTranslation();

  const localizedName = addon.name?.[i18n.language] ?? '';
  const defaultNameKey = getDefaultAddonNameTranslation(addon.type);
  const defaultName = defaultNameKey ? t(defaultNameKey) : '';
  const addonName = addon.displayName || localizedName || defaultName;

  const formattedPrice = formatCurrency({
    currency: addon.fixedPrice.currency,
    number: addon.fixedPrice.amount,
  });

  const isDragging = dragHandle?.isDragging ?? false;

  return (
    <div
      data-testid={dataTestId}
      className={classNames('flex items-center gap-4 py-3 px-3 rounded-sm', {
        'bg-tertiary': isDragging,
      })}>
      <div className='flex items-start gap-1 flex-1 min-w-0'>
        {dragHandle ? (
          <div
            {...dragHandle.listeners}
            {...dragHandle.attributes}
            tabIndex={-1}
            style={{ touchAction: 'none' }}
            className='flex items-center justify-center w-5 h-5 shrink-0 cursor-move'
            aria-label={t('offers:reorderSectionsActionLabel')}>
            <IconDrag className='text-tertiary w-5 h-5' />
          </div>
        ) : (
          <div className='w-5 h-5 shrink-0' aria-hidden />
        )}
        <div className='flex items-center gap-2 flex-1 min-w-0'>
          <InputCheckbox value={checked} name={addon.id} onChange={() => onToggle(addon.id)} disabled={disabled} />
          <label
            htmlFor={addon.id}
            className={classNames('truncate min-w-0 cursor-pointer', {
              'text-primary text-subtitle-s': checked,
              'text-secondary text-body-s': !checked,
            })}>
            {addonName}
          </label>
        </div>
      </div>
      <span
        className={classNames('shrink-0', {
          'text-primary text-subtitle-s': checked,
          'text-secondary text-body-s': !checked,
        })}>
        {formattedPrice}
      </span>
    </div>
  );
};
